import { TEXTAREA_CHANGED_DATA_ATTRIBUTE_KEY } from "@/src/shared/constants/warnLeavingUnsaved";

const SKIP_WARN_LEAVING_UNSAVED_ATTRIBUTE = "data-skip-warn-leaving-unsaved";
const WARN_LEAVING_UNSAVED_INPUT_SELECTOR = "input[data-warn-leaving-unsaved]";

/**
 * Class to manage warning users about unsaved changes when they attempt to leave the page.
 * This class provides functionality to detect changes in textareas or forms and trigger warnings accordingly.
 */
class WarnLeavingUnsaved {
  private forcedFormChangedCallbacks: (() => boolean)[] = [];

  /**
   * Initializes the warning system by adding listeners to forms and textareas,
   * and setting up the `beforeunload` event handler.
   */
  public init = (): void => {
    this.addDelegatedListeners();
    this.setBeforeUnload();
  };

  public disable = (): void => {
    window.onbeforeunload = null;
  };

  /** Queries the currently tracked state without blurring controls or initializing listeners. */
  public hasUnsavedChanges = (): boolean => {
    return this.checkIfUnsavedChangesExist() || this.forcedFormChanged();
  };

  public ownsBeforeUnloadHandler = (): boolean => {
    return window.onbeforeunload === this.beforeUnload;
  };

  /**
   * Adds a callback function that determines if a form should be considered as having unsaved changes.
   * @param callback - A function returning `true` if the form should be marked as changed.
   */
  public addForcedFormChangedCallback = (callback: () => boolean): void => {
    this.forcedFormChangedCallbacks.push(callback);
  };

  /**
   * Clears all unsaved change warnings, including resetting textarea states and removing forced callbacks.
   */
  public clearWarnLeavingUnsaved = (): void => {
    this.clearChangedState();
    this.forcedFormChangedCallbacks = [];
    (window as any).EasyEditor?.clearAllAutosaveState?.();
  };

  private addDelegatedListeners = (): void => {
    this.addDelegateListenersToForms();
    this.addDelegateListenersToTextareas();
    this.addDelegateListenersToInputs();
  };

  private addDelegateListenersToForms = (): void => {
    document.addEventListener("submit", event => {
      if ((event.target as HTMLFormElement).tagName === "FORM") {
        this.clearWarnLeavingUnsaved();
      }
    });
  };

  private addDelegateListenersToTextareas = (): void => {
    const trackChange = (event: Event): void => {
      const target = event.target as HTMLElement;
      if (target.tagName === "TEXTAREA" && !this.shouldSkipTextarea(target as HTMLTextAreaElement)) {
        this.markTextareaAsChanged(target as HTMLTextAreaElement);
      }
    };
    document.addEventListener("change", trackChange);
    document.addEventListener("input", trackChange);
  };

  private addDelegateListenersToInputs = (): void => {
    document.addEventListener("input", event => {
      const target = event.target;
      if (this.isTrackedInput(target)) {
        this.markInputAsChanged(target);
      }
    });
  };

  private setBeforeUnload = (): void => {
    window.onbeforeunload = this.beforeUnload;
  };

  private beforeUnload = (): string | undefined => {
    this.blurAllTextareas();
    if (this.hasUnsavedChanges()) {
      return window.I18n.textWarnLeavingUnsaved;
    }
  };

  blurAllTextareas = (): void => {
    const textareas = document.querySelectorAll<HTMLTextAreaElement>("textarea");
    textareas.forEach(textarea => textarea.blur());
  };

  forcedFormChanged = (): boolean => {
    return this.forcedFormChangedCallbacks.some(callback => callback());
  };

  private checkIfUnsavedChangesExist = (): boolean => {
    const textareas = document.querySelectorAll<HTMLTextAreaElement>("textarea");
    const inputs = document.querySelectorAll<HTMLInputElement>(WARN_LEAVING_UNSAVED_INPUT_SELECTOR);

    return (
      Array.from(textareas).some(this.checkTextareaUnsavedChangesExist) ||
      Array.from(inputs).filter(this.isTrackedInput).some(this.checkInputUnsavedChangesExist)
    );
  };

  private markTextareaAsChanged = (textarea: HTMLTextAreaElement): void => {
    textarea.dataset[TEXTAREA_CHANGED_DATA_ATTRIBUTE_KEY] = "true";
  };

  private checkTextareaUnsavedChangesExist = (textarea: HTMLTextAreaElement): boolean => {
    if (this.shouldSkipTextarea(textarea)) {
      return false;
    }

    const editorId = textarea.id;

    if (textarea.dataset[TEXTAREA_CHANGED_DATA_ATTRIBUTE_KEY]) {
      return true;
    }

    if (editorId && this.isCKEditorDirty(editorId)) {
      return true;
    }

    return false;
  };

  private markInputAsChanged = (input: HTMLInputElement): void => {
    input.dataset[TEXTAREA_CHANGED_DATA_ATTRIBUTE_KEY] = "true";
  };

  private checkInputUnsavedChangesExist = (input: HTMLInputElement): boolean => {
    return input.dataset[TEXTAREA_CHANGED_DATA_ATTRIBUTE_KEY] === "true";
  };

  private isTrackedInput = (element: EventTarget | null): element is HTMLInputElement => {
    return (
      element instanceof HTMLInputElement &&
      element.matches(WARN_LEAVING_UNSAVED_INPUT_SELECTOR) &&
      element.type === "text"
    );
  };

  private shouldSkipTextarea = (textarea: HTMLTextAreaElement): boolean => {
    return textarea.hasAttribute(SKIP_WARN_LEAVING_UNSAVED_ATTRIBUTE);
  };

  private isCKEditorDirty = (editorId: string): boolean => {
    return !!window.CKEDITOR?.isDirty?.[editorId];
  };

  private clearChangedState = (): void => {
    const textareas = document.querySelectorAll<HTMLTextAreaElement>("textarea");
    textareas.forEach(textarea => delete textarea.dataset[TEXTAREA_CHANGED_DATA_ATTRIBUTE_KEY]);
    const inputs = document.querySelectorAll<HTMLInputElement>(WARN_LEAVING_UNSAVED_INPUT_SELECTOR);
    Array.from(inputs)
      .filter(this.isTrackedInput)
      .forEach(input => delete input.dataset[TEXTAREA_CHANGED_DATA_ATTRIBUTE_KEY]);

    if (window.CKEDITOR && window.CKEDITOR.isDirty) {
      Object.keys(window.CKEDITOR.isDirty).forEach((editorId: string) => {
        window.CKEDITOR.isDirty[editorId] = false;
        const editor = window.CKEDITOR.instances?.[editorId];
        if (editor) {
          window.CKEDITOR.initialValue[editorId] = editor.getData();
        }
      });
    }
  };
}

const warnLeavingUnsaved = new WarnLeavingUnsaved();

export const initWarnLeavingUnsaved = (): void => {
  warnLeavingUnsaved.init();
};

export default warnLeavingUnsaved;
